import { useState } from 'react';
import {
  Button,
  Icon,
  useDisclosure,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  VStack,
  HStack,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import { RiArrowGoBackLine, RiAlertLine } from 'react-icons/ri';

import { ModalComponent } from '..';
import { IExpenseReport } from '../../../../pages/Dashboard/vehicles_management/Expenses';
import { expenseReportsService } from '../../../../services/expenseReportsService';
import { apllyToast } from '../../../Global/Toast2.0';
import { useCan } from '../../../../hooks/useCan';

interface IModalProps {
  report: IExpenseReport;
  onReverted: () => void;
}

export function ModalRevertRessarcimento({ report, onReverted }: IModalProps) {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const [observation, setObservation] = useState('');
  const [saving, setSaving] = useState(false);

  // Gate: apenas quem tem a permission "Reverter Ressarcimento".
  const canRevert = useCan({ permissions: ['edit_ressarcimento'] });

  const handleOpen = () => {
    setObservation(report.observation || '');
    onOpen();
  };

  const handleRevert = async () => {
    if (observation.trim().length < 10) {
      apllyToast(
        'warning',
        'Descreva o motivo da reversão com pelo menos 10 caracteres',
      );
      return;
    }

    setSaving(true);
    try {
      await expenseReportsService.revert(report.id, {
        observation: observation.trim(),
      });
      apllyToast('success', 'Ressarcimento revertido para edição!');
      onReverted();
      onClose();
    } catch (err: any) {
      apllyToast(
        'error',
        err.response?.data?.message || 'Erro ao reverter o ressarcimento',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!canRevert) {
    return null;
  }

  return (
    <>
      <Tooltip
        hasArrow
        label="Reverter ressarcimento (voltar status e assinaturas)"
        placement="top"
      >
        <Button
          size="sm"
          colorScheme="orange"
          variant="ghost"
          onClick={handleOpen}
        >
          <Icon as={RiArrowGoBackLine} fontSize="20" />
        </Button>
      </Tooltip>

      <ModalComponent
        title="Reverter Ressarcimento"
        isOpen={isOpen}
        onClose={onClose}
        sizeSpecifyc="xl"
      >
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <HStack
              spacing={3}
              bg="whiteAlpha.100"
              p={3}
              borderRadius="md"
              borderLeft="3px solid"
              borderLeftColor="orange.400"
              align="start"
            >
              <Icon
                as={RiAlertLine}
                color="orange.300"
                fontSize="20"
                mt="1px"
              />
              <Box>
                <Text fontSize="sm" color="orange.200" fontWeight="bold">
                  Esta ação reabre o relatório para edição
                </Text>
                <Text fontSize="xs" color="gray.300" mt={1}>
                  As assinaturas do responsável, do gestor e dos envolvidos
                  voltarão para <b>pendente</b> e o status contábil retornará
                  para <b>Em Processo</b>. Os e-mails de assinatura precisarão
                  ser reenviados.
                </Text>
              </Box>
            </HStack>

            <Box>
              <Text fontSize="sm" color="gray.300" mb={1}>
                Motivo da reversão{' '}
                <Text as="span" color="red.300">
                  *
                </Text>
              </Text>
              <Textarea
                value={observation}
                onChange={e => setObservation(e.target.value)}
                placeholder="Ex.: reabrir para adicionar um envolvido no ressarcimento"
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.300"
                color="gray.100"
                rows={4}
              />
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={saving}>
            Cancelar
          </Button>
          <Button
            colorScheme="orange"
            onClick={handleRevert}
            isLoading={saving}
            leftIcon={<Icon as={RiArrowGoBackLine} />}
          >
            Reverter
          </Button>
        </ModalFooter>
      </ModalComponent>
    </>
  );
}
